"use client";

import { type Dispatch, type SetStateAction, createContext, useContext } from "react";

/**
 * Used shortcuts:
 * - Command or Ctrl + K: Open command
 * - Backspace: Close command
 * - Enter: Clear input
 * - Shift + C: Copy cart
 * - Shift + D: Share cart
 * - Shift + N: New cart
 * - Shift + S: Save project dialog
 * - Shift + M: Import products dialog
 * - Shift + F: Export cart to CSV file
 * - Shift + P: Download picture (furniture)
 * - Shift + G: Download gltf (furniture)
 * - Shift + X: Toggle debug (furniture)
 * - Shift + E: Export (furniture)
 * - Shift + I: Import (furniture)
 * - Shift + U: Dimensions Unit (furniture)
 */

export type CMDKPage =
	| "home"
	| "cart"
	| "language"
	| "product"
	| "mainCategory"
	| "furnitureCategory"
	| "accessoriesCategory";

export type CommandDialogType =
	| "saveProject"
	| "importProducts_list"
	| "importProducts_file"
	| "postcode";

interface CommandInterface {
	open: boolean;
	onOpenChangeHandler: (open?: boolean) => void;
	inputValue: string;
	setInputValue: Dispatch<SetStateAction<string>>;
	pages: CMDKPage[];
	setPages: Dispatch<SetStateAction<CMDKPage[]>>;
	activePage?: CMDKPage;
	copyCartCallback: () => Promise<void>;
	shareCartCallback: () => Promise<void>;
	newCartCallback: () => Promise<void>;
	saveProjectCallback: () => Promise<void>;
	importProductsCallback: () => Promise<void>;
	exportCartToCSVCallback: () => Promise<void>;
	isDialogActive: (value: CommandDialogType) => boolean;
	activeDialog?: CommandDialogType;
	setActiveDialog: Dispatch<SetStateAction<CommandDialogType | undefined>>;
}

export const CommandContext = createContext<CommandInterface>({} as CommandInterface);

export function useCommand() {
	const context = useContext(CommandContext);

	if (context === null) throw new Error("Command context is not available");

	return context;
}
